import axios from "axios";
import { getItem, setItem, removeItem } from "../utils/localStorage";

const baseUrl = process.env.REACT_APP_BASE_URL;

export const ACCESS_TOKEN_KEY = "token";
export const USERNAME_KEY = "username";
// Non-sensitive marker indicating a session likely exists (the real refresh
// token is an httpOnly cookie JS cannot read). Used to decide whether to
// attempt a silent refresh on load, avoiding a pointless 401 after logout.
export const HAS_SESSION_KEY = "hasSession";

export const getAccessToken = () => getItem(ACCESS_TOKEN_KEY);
export const setAccessToken = (token) => {
  setItem(ACCESS_TOKEN_KEY, token);
  setItem(HAS_SESSION_KEY, "true");
};

export const hasStoredSession = () => Boolean(getItem(HAS_SESSION_KEY));

export const clearAuthStorage = () => {
  removeItem(ACCESS_TOKEN_KEY);
  removeItem(USERNAME_KEY);
  removeItem(HAS_SESSION_KEY);
};

// Single axios instance used by the whole app. withCredentials lets the
// httpOnly refresh cookie ride along on /auth/login and /auth/refresh.
export const apiClient = axios.create({
  baseURL: baseUrl,
  withCredentials: true,
});

// Attach the current access token to every outgoing request.
apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers["x-access-token"] = token;
  }
  return config;
});

// Single-flight refresh: concurrent 401s share one /auth/refresh call.
let refreshPromise = null;

export const refreshToken = () => {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${baseUrl}/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        const newToken = res?.data?.token;
        if (!newToken) {
          throw new Error("No token returned from refresh");
        }
        setAccessToken(newToken);
        const username = res?.data?.user?.username;
        if (username) {
          setItem(USERNAME_KEY, username.split(" ")[0]);
        }
        return newToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

export const redirectToSignIn = () => {
  clearAuthStorage();
  if (typeof window !== "undefined" && window.location.pathname !== "/sign-in") {
    window.location.assign("/sign-in");
  }
};

export const isAuthEndpoint = (url = "") =>
  url.includes("/auth/refresh") ||
  url.includes("/auth/login") ||
  url.includes("/auth/logout");

// On 401, silently refresh once and retry the original request. If the refresh
// itself fails, the session is truly over -> redirect to sign-in.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;

    if (
      status === 401 &&
      original &&
      !original._retry &&
      !isAuthEndpoint(original.url)
    ) {
      original._retry = true;
      try {
        const newToken = await refreshToken();
        original.headers = original.headers || {};
        original.headers["x-access-token"] = newToken;
        return apiClient(original);
      } catch (refreshError) {
        redirectToSignIn();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

// --- Query functions -------------------------------------------------------
// All request functions live here and use the shared axios client. Components
// call these from useQuery directly. The react-query `signal` is forwarded so
// stale in-flight requests get cancelled.

// Summary of hospital counts per global-filter bucket. Effectively static, so
// consumers cache it aggressively.
export const getHospitalSummary = async (signal) => {
  try {
    const { data } = await apiClient.get("/new_data/hospitals/summary", {
      signal,
    });
    return data;
  } catch (error) {
    console.error("Error fetching hospital summary:", error);
    throw error;
  }
};

// Per-card hospital counts for the current filter selection. The request body
// is part of the query key, so identical filter combinations share one fetch.
export const getCardFilterCounts = async (body, signal) => {
  try {
    const { data } = await apiClient.post(
      "/new-data/card-filter-counts",
      body,
      { signal },
    );
    return data;
  } catch (error) {
    console.error("Error fetching card filter counts:", error);
    throw error;
  }
};

// Per-state hospital counts + case density + partner flag for the hex map.
export const getCountByState = async (params, signal) => {
  const { partnerExclusiveFlag, globalFilter, quickFilter } = params;
  try {
    const { data } = await apiClient.get("/new_data/hospitals/count_by_state", {
      params: {
        partnerExclusiveArea: String(partnerExclusiveFlag),
        globalFilter: globalFilter ?? "all",
        ...(quickFilter ? { quickFilter } : {}),
      },
      signal,
    });
    return data;
  } catch (error) {
    console.error("Error fetching count by state:", error);
    throw error;
  }
};

// Single hospital detail by provider number / id.
export const getHospitalDetail = async (id, signal) => {
  try {
    const { data } = await apiClient.get(`/new_data/hospitals/${id}`, {
      signal,
    });
    return data;
  } catch (error) {
    console.error("Error fetching hospital detail:", error);
    throw error;
  }
};

// Generic hospitals-list read. Accepts the absolute URL built by
// buildHospitalsUrl (axios ignores baseURL for absolute URLs), so the table,
// text-search, state-filter and advanced-filter list fetches share one
// transport. The URL fully encodes the filter context and doubles as the key.
export const getHospitalsByUrl = async (url, signal) => {
  try {
    const { data } = await apiClient.get(url, { signal });
    return data;
  } catch (error) {
    console.error("Error fetching hospitals list:", error);
    throw error;
  }
};

// Full list for the comparison dropdown.
export const getAllHospitals = async ({ page = 1, perPage = 1000 } = {}, signal) => {
  try {
    const { data } = await apiClient.get("/new_data/hospitals/all", {
      params: { page, per_page: perPage },
      signal,
    });
    return data;
  } catch (error) {
    console.error("Error fetching all hospitals:", error);
    throw error;
  }
};

// Side-by-side comparison data for the selected provider numbers.
export const getCompareHospitals = async (providerNumbers = [], signal) => {
  try {
    const query = (providerNumbers || [])
      .map((pn) => `provider_number=${encodeURIComponent(pn)}`)
      .join("&");
    const { data } = await apiClient.get(
      `/new_data/hospitals/compare_hospitals?${query}`,
      { signal },
    );
    return data;
  } catch (error) {
    console.error("Error fetching compare hospitals:", error);
    throw error;
  }
};

// Hospital text search (autocomplete / suggestions).
export const searchHospitals = async (searchTerm, signal) => {
  try {
    const { data } = await apiClient.get("/new_data/hospitals/search", {
      params: { search_term: searchTerm },
      signal,
    });
    return data;
  } catch (error) {
    console.error("Error searching hospitals:", error);
    throw error;
  }
};

// Email the selected bookmark data to recipients.
export const sendBookmarkEmail = async (payload) => {
  try {
    const { data } = await apiClient.post("/email/send-email", payload);
    return data;
  } catch (error) {
    console.error("Error sending bookmark email:", error);
    throw error;
  }
};

// --- Bookmarks -------------------------------------------------------------

export const getBookmarks = async (signal) => {
  try {
    const { data } = await apiClient.get("/bookmark/bookmarks", { signal });
    return data;
  } catch (error) {
    console.error("Error fetching bookmarks:", error);
    throw error;
  }
};

// Returns the full axios response so callers can inspect status (207/201).
export const addBookmarkApi = async (providerNumbers = []) => {
  try {
    const list = Array.isArray(providerNumbers)
      ? providerNumbers
      : [providerNumbers];
    const query = list
      .map((pn) => `provider_number=${encodeURIComponent(pn)}`)
      .join("&");
    return await apiClient.post(`/bookmark/add-bookmark?${query}`);
  } catch (error) {
    console.error("Error adding bookmark:", error);
    throw error;
  }
};

// Returns the full axios response so callers can inspect status (200/404).
export const removeBookmarkApi = async (providerNumbers = []) => {
  try {
    const list = Array.isArray(providerNumbers)
      ? providerNumbers
      : [providerNumbers];
    const query = list
      .map((pn) => `provider_number=${encodeURIComponent(pn)}`)
      .join("&");
    return await apiClient.delete(`/bookmark/remove-bookmark?${query}`);
  } catch (error) {
    console.error("Error removing bookmark:", error);
    throw error;
  }
};

// --- Sorting model ---------------------------------------------------------

export const createSortingModel = async (body, signal) => {
  try {
    const { data } = await apiClient.post(
      "/sorting-model/create_sorting_model",
      body,
      { signal },
    );
    return data;
  } catch (error) {
    console.error("Error creating sorting model:", error);
    throw error;
  }
};

// Sorting results read. Accepts the absolute URL built by the caller.
export const getSortingResultsByUrl = async (url, signal) => {
  try {
    const { data } = await apiClient.get(url, { signal });
    return data;
  } catch (error) {
    console.error("Error fetching sorting results:", error);
    throw error;
  }
};

export const clearHospitalCache = async () => {
  try {
    const { data } = await apiClient.post("/sorting-model/clear_cache");
    return data;
  } catch (error) {
    console.error("Error clearing hospital cache:", error);
    throw error;
  }
};

export const getSortingCacheInfo = async (signal) => {
  try {
    const { data } = await apiClient.get("/sorting-model/cache_info", {
      signal,
    });
    return data;
  } catch (error) {
    console.error("Error fetching sorting cache info:", error);
    throw error;
  }
};

// Top 25 hospitals for the right-hand panel, keyed on the active filter + sort.
export const getTop25Hospitals = async (params, signal) => {
  const {
    sortBy,
    sortOrder,
    partnerExclusiveFlag,
    globalFilter,
    states,
    quickFilter,
  } = params;
  try {
    const { data } = await apiClient.get(
      "/new_data/hospitals/top_25_hospitals",
      {
        params: {
          sort_by: sortBy,
          sort_order: sortOrder,
          partnerExclusiveArea: String(partnerExclusiveFlag),
          globalFilter: globalFilter ?? "all",
          ...(states ? { states } : {}),
          ...(quickFilter ? { quickFilter } : {}),
        },
        signal,
      },
    );
    return data;
  } catch (error) {
    console.error("Error fetching top 25 hospitals:", error);
    throw error;
  }
};

export default apiClient;
